<i18n>
{
	"en": {
		"selectVendor": "Select vendor",
		"vendorsSelected": "All vendors | {count} vendor selected | {count} vendors selected",
		"vendorIsPreferred": "Vendor is preferred for order pickup postal code.",
		"vendorHasEdi": "Vendor supports EDI messages."
	},
	"no": {
		"selectVendor": "Velg leverandør",
		"vendorsSelected": "Alle leverandør | {count} leverandør valgt | {count} leverandører valgt",
		"vendorIsPreferred": "Leverandørens postnummer er foretrukket for henting av oppdrag.",
		"vendorHasEdi": "Leverandør støtter EDI-meldinger."
	}
}
</i18n>
<template>
	<t-select
		:items="vendorsDisplay"
		:label="label"
		:selectedItem="selectedItem"
		:selectedItems="selectedItems"
		:disabled="disabled"
		width="100%"
		v-bind="$attrs"
		relative
		@change="onChange"
	>
		<template #displayedValue="{popoverShow}">
			{{ getDisplayedValue }}
		</template>
		<template #optionItem="{ item, isSelected }">

			<t-menu-combobox-option-item
				:value="item"
				:selected="isSelected"
				:class="{preferred: item.isPreferred}"
			>
				<TToolTip flex>
					<template slot="target">
						<div class="item">
							<div class="item-main-text">{{ item.name }}</div>
							<div class="item-sub-text">{{ item.vendorNumber }}</div>
						</div>
						<div class="item-detail-icon">
							<t-timpex-icon
								v-show="item.edipartId"
								:class="[{selected: isSelected}]"
								title="EDI"
								icon="edi"
								type="bp"
								size="xs"
								class="menu-icon-option-item-tick"
							/>
						</div>
					</template>
					<div style="padding:0 5px">
						<div v-if="item.isPreferred">{{ $t('vendorIsPreferred')}}</div>
						<div v-if="item.edipartId">{{ $t('vendorHasEdi')}}</div>
					</div>
				</TToolTip>
			</t-menu-combobox-option-item>
		</template>
	</t-select>
</template>

<script>
import { mapState, mapActions } from './store/vendors.store.js';
import formMixin from '@/components/evergreen/inputs/formMixin';
import popoverMixin from '@/components/evergreen/inputs/popoverMixin';
import inputWrapperMixin from '@/components/evergreen/inputs/inputWrapperMixin';
import deepCopy from 'deepcopy';

export default {
	name: 'VendorSelect',
	mixins: [ formMixin,popoverMixin,inputWrapperMixin ],
	props: {
		title: String,
		selectedItem: Object,
		selectedItems: Array,
		appearance: String,
		intent: String,
		preferredFirst: Boolean,
		filterPostalCodes: Array
	},
	data() {
		return {
			searchValue: '',
			internalSelected: undefined,
			lastSearch: null,
			vendorsDisplay: []
		};
	},
	computed: {
		...mapState(['vendors','preferredVendors']),
		getDisplayedValue() {
			if (this.selectedItems && this.selectedItems.length > 0) {
				return this.$tc('vendorsSelected', this.selectedItems.length);
			}
			return this.selectedItem ? this.getSelectedItemValue : this.$t('selectVendor');
		},
		getSelectedItemValue() {
			return `${this.selectedItem.name} (${this.selectedItem.vendorNumber})`;
		},
	},
	async mounted() {
		await this.getVendors();
		this.vendorsDisplay = deepCopy(this.vendors.filter(x => x.type === 'CARRIER'));

		if (this.preferredFirst && this.filterPostalCodes.length > 0) {
			await this.getPreferredVendors();
			let lookup = {};
			for (let vendor of this.vendorsDisplay)
				lookup[vendor.id] = { vendor, score: Number.MAX_SAFE_INTEGER };

			this.preferredVendors.forEach(x => {
				let matches = this.filterPostalCodes.some( code => code >= x.startPostalRange && code <= x.endPostalRange);
				if (matches) {
					x.vendors.forEach((v, i) => {
						lookup[v].score = Math.min(i, lookup[v].score);
					});
				}
			});

			this.vendorsDisplay.forEach(x => {
				x.isPreferred = lookup[x.id].score !== Number.MAX_SAFE_INTEGER;
			});
			this.vendorsDisplay.sort((a,b) => {
				let score = lookup[a.id].score - lookup[b.id].score;
				if (score === 0) {
					if (a.name < b.name) return -1;
					else if (a.name > b.name) return 1;
				}
				return score;
			});

		}
	},
	methods: {
		...mapActions(['getVendors', 'getPreferredVendors']),
		onChange(value) {
			let retVal = this.vendors.find( x => x.id === value.id);
			this.popoverSetHidden();
			this.$emit('change', retVal);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';

.wrapper {
	position: relative;
	display: flex;
}

.icon-wrapper {
	pointer-events: none;
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 32px;
	height: 100%;
	z-index: 3;
}

.icon {
	height: 14px;
	width: 14px;
	color: get-color($icon, 'default');
}

.item {
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.item-detail-icon {
	display: flex;
	justify-content: center;
	padding-left: 8px;
	padding-right: 12px;
	align-self: center;
}

.preferred {
	background-color: rgba(71, 184, 129, 0.2);
	&:hover {
		background-color: rgba(71, 184, 129, 0.3);
	}
	&.selected, &:active {
		background-color: rgba(71, 184, 129, 0.35);
	}
	&.focused, &:focus {
		background-color: rgba(71, 184, 129, 0.3);
	}

	/deep/ .menu-combobox-option-item-inner {
		color: get-color($green, 'dark');
	}

	/deep/ .menu-combobox-option-item-icon {
		color: get-color($green, 'dark');
	}
}

.menu-combobox-option-item-icon {
	color: get-color($icon, 'default');
	margin-left: 0;
	margin-right: 0;
	height: 14px;
	width: 14px;
	box-sizing: border-box;
	&.selected {
		color: get-color($blue, 'base');
	}
	&.focused {
		color: get-color($blue, 'dark');
	}
}
</style>
